import services from '@/services/ascentiaServiceIndex'
import { DPLF_CONSTS as DC } from '../consts'
import {
  CODE_FLIGHT_PHASES,
  CODE_STATES,
  CODE_PERSISTENCES,
} from '@/utilities/appConsts'
import _ from 'lodash'

const state = {
  ataChapters: [],
  faultMsgCountByTailData: [],
  faultMsgCountByAtaChapterData: [],
  faultMsgCountByParaData: [],
  faultMsgCodesData: [],
  faultReportsByFlightData: [],
  messageCodes: [],
  faultMsgRawReportData: '',
  faultMsgDetailsData: [],
  visualizeDetailsData: {},
}

const getters = {
  mappedDplfAtaChapters: state => {
    return state.ataChapters.map(item => {
      return {
        text: `${item.ataChapter} - ${item.description}`,
        value: item.ataChapter,
      }
    })
  },

  mappedFaultMsgCountByTailData: state => {
    const rawData = state.faultMsgCountByTailData
    const data = rawData.map(item => {
      return {
        name: item.tailNumber,
        flightDeckEffects: item.fdEcount,
        correlatedMaintenance: item.cmMcount,
        nonCorrelatedMaintenance: item.ncmMcount,
        maintenanceMemos: item.mmOcount,
        total: item.fdEcount + item.cmMcount + item.ncmMcount + item.mmOcount,
      }
    })

    const filteredSortedData = _.orderBy(data, ['total'], ['desc']).filter(
      item => item.total > 0,
    )

    return filteredSortedData
  },

  mappedFaultMsgCountByAtaChapterData: (
    state,
    getters,
    rootState,
    rootGetters,
  ) => {
    const ataChapters = rootGetters['lookups/mappedLuAtaChapters']
    const rawData = state.faultMsgCountByAtaChapterData
    const data = rawData.map(item => {
      const [ataChapter] = ataChapters.filter(
        ata => ata.ataChapterId === item.ataChapter,
      )
      const ataChapterDesc = ataChapter ? ` - ${ataChapter.ataChapterDesc}` : ''

      return {
        name: item.ataChapter.toString() + ataChapterDesc,
        flightDeckEffects: item.fdEcount,
        correlatedMaintenance: item.cmMcount,
        nonCorrelatedMaintenance: item.ncmMcount,
        maintenanceMemos: item.mmOcount,
        total: item.fdEcount + item.cmMcount + item.ncmMcount + item.mmOcount,
      }
    })

    const filteredSortedData = _.orderBy(data, ['total'], ['desc']).filter(
      item => item.total > 0,
    )

    return filteredSortedData
  },

  mappedFaultMsgCountByParaData: (state, getters, rootState, rootGetters) => {
    const ataSubchapters = rootGetters['lookups/mappedLuAtaSubchapters']
    const rawData = state.faultMsgCountByParaData
    const data = rawData.map(item => {
      const ataSubchapterId = item.para.toString().charAt(0) + '0'
      const [ataSubchapter] = ataSubchapters.filter(
        ata =>
          ata.ataChapterId === item.ataChapter &&
          ata.ataSubchapterId.toString() === ataSubchapterId,
      )
      const ataSubchapterDesc = ataSubchapter
        ? `${ataSubchapter.ataSubchapterDesc}`
        : ''

      return {
        name: `${item.ataChapter.toString()} - ${item.para.toString()} - ${ataSubchapterDesc}`,
        flightDeckEffects: item.fdEcount,
        correlatedMaintenance: item.cmMcount,
        nonCorrelatedMaintenance: item.ncmMcount,
        maintenanceMemos: item.mmOcount,
        total: item.fdEcount + item.cmMcount + item.ncmMcount + item.mmOcount,
      }
    })

    const filteredSortedData = _.orderBy(data, ['total'], ['desc']).filter(
      item => item.total > 0,
    )

    return filteredSortedData
  },

  mappedFaultMsgCodesData: state => {
    const items = []
    let rowId = 1

    state.faultMsgCodesData.forEach(item => {
      const flightNumber = item.flightNumber

      // fdes
      const fdes = item.flightDeckEffects || []
      fdes.forEach(fde => {
        const children = []

        const cmms = item.correlatedMaintenanceMessages.filter(
          cmm => cmm.parentId === fde.id,
        )

        cmms.forEach(cm => {
          const cmItem = {
            id: rowId,
            airlineIcao: item.airlineIcao,
            errorType: 'CMM',
            code: cm.code,
            flightPhase: CODE_FLIGHT_PHASES[cm.flightPhaseShortName] || '-',
            timeStamp: cm.timeAlerted + 'Z',
            flightNumber: flightNumber,
            state: CODE_STATES[cm.state],
            persistence: CODE_PERSISTENCES[cm.persistence] || '-',
            fileLogId: item.fileLogId,
            ataChapter: item.ataChapter,
            tailNumber: item.tailNumber,
          }
          children.push(cmItem)
          rowId = rowId + 1
        })

        const updatedItem = {
          id: rowId,
          airlineIcao: item.airlineIcao,
          errorType: 'FDE',
          code: fde.code,
          flightPhase: CODE_FLIGHT_PHASES[fde.flightPhaseShortName] || '-',
          flightNumber,
          timeStamp: fde.timeAlerted + 'Z',
          state: CODE_STATES[fde.state],
          persistence: CODE_PERSISTENCES[fde.persistence] || '-',
          fileLogId: item.fileLogId,
          ataChapter: item.ataChapter,
          tailNumber: item.tailNumber,
          children,
        }
        items.push(updatedItem)
        rowId = rowId + 1
      })

      // ncmms
      const ncmms = item.nonCorrelatedMaintenanceMessages || []
      ncmms.forEach(ncmm => {
        const updatedItem = {
          id: rowId,
          airlineIcao: item.airlineIcao,
          errorType: 'NCMM',
          code: ncmm.code,
          flightPhase: CODE_FLIGHT_PHASES[ncmm.flightPhaseShortName],
          flightNumber,
          timeStamp: ncmm.timeAlerted + 'Z',
          state: CODE_STATES[ncmm.state],
          persistence: CODE_PERSISTENCES[ncmm.persistence] || '-',
          fileLogId: item.fileLogId,
          ataChapter: item.ataChapter,
          tailNumber: item.tailNumber,
        }
        items.push(updatedItem)
        rowId = rowId + 1
      })

      // mmos
      const mmos = item.maintenanceMemos || []
      mmos.forEach(mmo => {
        const children = []

        const cmms = item.correlatedMaintenanceMessages.filter(
          cmm => cmm.parentId === mmo.id,
        )

        cmms.forEach(cm => {
          const cmItem = {
            id: rowId,
            airlineIcao: item.airlineIcao,
            errorType: 'CMM',
            code: cm.code,
            flightPhase: CODE_FLIGHT_PHASES[cm.flightPhaseShortName],
            timeStamp: cm.timeAlerted + 'Z',
            flightNumber: flightNumber,
            state: CODE_STATES[cm.state],
            persistence: CODE_PERSISTENCES[cm.persistence] || '-',
            fileLogId: item.fileLogId,
            ataChapter: item.ataChapter,
            tailNumber: item.tailNumber,
          }
          children.push(cmItem)
          rowId = rowId + 1
        })

        const updatedItem = {
          id: rowId,
          airlineIcao: item.airlineIcao,
          errorType: 'MMO',
          code: mmo.code,
          flightPhase: CODE_FLIGHT_PHASES[mmo.flightPhaseShortName] || '-',
          flightNumber,
          timeStamp: mmo.timeAlerted + 'Z',
          state: CODE_STATES[mmo.state],
          persistence: CODE_PERSISTENCES[mmo.persistence] || '-',
          fileLogId: item.fileLogId,
          ataChapter: item.ataChapter,
          tailNumber: item.tailNumber,
          children,
        }
        items.push(updatedItem)
        rowId = rowId + 1
      })
    })

    const newArray = []

    items.forEach(parent => {
      if (parent.children?.length) {
        parent.children.forEach(child => {
          newArray.push({
            ...child,
            path: [parent.id, child.id],
          })
        })
      }
      newArray.push({
        ...parent,
        path: [parent.id],
      })
    })

    return newArray
  },

  mappedFaultReportsByFlightData: state => {
    return state.faultReportsByFlightData
  },

  mappedDplfMessageCodes: state => {
    if (state.messageCodes) {
      return state.messageCodes.map(item => {
        return {
          text: item,
          value: item,
        }
      })
    }
    return []
  },

  mappedFaultMsgRawReport: state => {
    return state.faultMsgRawReportData
  },

  mappedFaultMsgDetails: state => {
    const [item] = state.faultMsgDetailsData

    if (!item) {
      return []
    }

    const items = []
    let rowId = 1

    const flightNumber = item.flightNumber

    // fdes
    const fdes = item.flightDeckEffects || []
    fdes.forEach(fde => {
      const children = []

      const cmms = item.correlatedMaintenanceMessages.filter(
        cmm => cmm.parentId === fde.id,
      )

      cmms.forEach(cm => {
        const cmItem = {
          id: rowId,
          airlineIcao: item.airlineIcao,
          errorType: 'CMM',
          code: cm.code,
          flightPhase: CODE_FLIGHT_PHASES[cm.flightPhaseShortName] || '-',
          timeStamp: cm.timeAlerted + 'Z',
          flightNumber: flightNumber,
          state: CODE_STATES[cm.state],
          persistence: CODE_PERSISTENCES[cm.persistence] || '-',
          fileLogId: item.fileLogId,
          ataChapter: item.ataChapter,
          tailNumber: item.tailNumber,
        }
        children.push(cmItem)
        rowId = rowId + 1
      })

      const updatedItem = {
        id: rowId,
        airlineIcao: item.airlineIcao,
        errorType: 'FDE',
        code: fde.code,
        flightPhase: CODE_FLIGHT_PHASES[fde.flightPhaseShortName] || '-',
        flightNumber,
        timeStamp: fde.timeAlerted + 'Z',
        state: CODE_STATES[fde.state],
        persistence: CODE_PERSISTENCES[fde.persistence] || '-',
        fileLogId: item.fileLogId,
        ataChapter: item.ataChapter,
        tailNumber: item.tailNumber,
        children,
      }
      items.push(updatedItem)
      rowId = rowId + 1
    })

    // ncmms
    const ncmms = item.nonCorrelatedMaintenanceMessages || []
    ncmms.forEach(ncmm => {
      const updatedItem = {
        id: rowId,
        airlineIcao: item.airlineIcao,
        errorType: 'NCMM',
        code: ncmm.code,
        flightPhase: CODE_FLIGHT_PHASES[ncmm.flightPhaseShortName],
        flightNumber,
        timeStamp: ncmm.timeAlerted + 'Z',
        state: CODE_STATES[ncmm.state],
        persistence: CODE_PERSISTENCES[ncmm.persistence] || '-',
        fileLogId: item.fileLogId,
        ataChapter: item.ataChapter,
        tailNumber: item.tailNumber,
      }
      items.push(updatedItem)
      rowId = rowId + 1
    })

    // mmos
    const mmos = item.maintenanceMemos || []
    mmos.forEach(mmo => {
      const children = []

      const cmms = item.correlatedMaintenanceMessages.filter(
        cmm => cmm.parentId === mmo.id,
      )

      cmms.forEach(cm => {
        const cmItem = {
          id: rowId,
          airlineIcao: item.airlineIcao,
          errorType: 'CMM',
          code: cm.code,
          flightPhase: CODE_FLIGHT_PHASES[cm.flightPhaseShortName],
          timeStamp: cm.timeAlerted + 'Z',
          flightNumber: flightNumber,
          state: CODE_STATES[cm.state],
          persistence: CODE_PERSISTENCES[cm.persistence] || '-',
          fileLogId: item.fileLogId,
          ataChapter: item.ataChapter,
          tailNumber: item.tailNumber,
        }
        children.push(cmItem)
        rowId = rowId + 1
      })

      const updatedItem = {
        id: rowId,
        airlineIcao: item.airlineIcao,
        errorType: 'MMO',
        code: mmo.code,
        flightPhase: CODE_FLIGHT_PHASES[mmo.flightPhaseShortName] || '-',
        flightNumber,
        timeStamp: mmo.timeAlerted + 'Z',
        state: CODE_STATES[mmo.state],
        persistence: CODE_PERSISTENCES[mmo.persistence] || '-',
        fileLogId: item.fileLogId,
        ataChapter: item.ataChapter,
        tailNumber: item.tailNumber,
        children,
      }
      items.push(updatedItem)
      rowId = rowId + 1
    })

    const newArray = []

    items.forEach(parent => {
      if (parent.children?.length) {
        parent.children.forEach(child => {
          newArray.push({
            ...child,
            path: [parent.id, child.id],
          })
        })
      }
      newArray.push({
        ...parent,
        path: [parent.id],
      })
    })

    return newArray
  },

  mappedVisualizeDetails: state => {
    return state.visualizeDetailsData
  },
}

const mutations = {
  [DC.UPDATE_FAULT_MSG_COUNT_BY_TAIL]: (state, data) =>
    (state.faultMsgCountByTailData = data),
  [DC.UPDATE_FAULT_MSG_COUNT_BY_ATA_CHAPTER]: (state, data) =>
    (state.faultMsgCountByAtaChapterData = data),
  [DC.UPDATE_FAULT_MSG_COUNT_BY_PARA]: (state, data) =>
    (state.faultMsgCountByParaData = data),
  [DC.UPDATE_FAULT_MSG_CODES]: (state, data) =>
    (state.faultMsgCodesData = data),
  [DC.UPDATE_MESSAGE_CODES]: (state, data) => (state.messageCodes = data),
  [DC.UPDATE_ATA_CHAPTER_OPTIONS]: (state, data) => {
    state.ataChapters = data
  },
  [DC.UPDATE_FAULT_REPORTS_BY_FLIGHT]: (state, data) =>
    (state.faultReportsByFlightData = data),
  [DC.UPDATE_FAULT_MSG_RAW_REPORT]: (state, data) =>
    (state.faultMsgRawReportData = data),
  [DC.UPDATE_FAULT_MSG_DETAILS]: (state, data) =>
    (state.faultMsgDetailsData = data),
  [DC.UPDATE_VISUALIZE_DETAILS]: (state, data) =>
    (state.visualizeDetailsData = data),
  [DC.RESET_DPLF_VALUES]: state => {
    state.messageCodes = []
  },
}

const actions = {
  async [DC.GET_FAULT_MSG_COUNT_BY_TAIL]({ commit, rootGetters }, options) {
    try {
      // get global filter, user settings and lookups data
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const gfSelections = rootGetters['globalFilter/selections']
      const airlinesData = rootGetters['user/mappedUserAirlines']
      const phmAirlinesData =
        rootGetters['user/mappedUserAirlinesWithPhmAgreements']
      const tailsData = rootGetters['lookups/mappedLuTails']

      // set allowed airlineIds - airlineIds with Phm Agreements
      const allowedAirlineIds = phmAirlinesData.map(item => item.airlineId)

      // get filtered tails
      const filteredTails = tailsData
        .filter(item => allowedAirlineIds.includes(item.airlineId))
        .filter(
          item =>
            gfSelections.length === 0 ||
            gfSelections.filter(
              selection =>
                selection.airlineId === item.airlineId &&
                selection.masterModelId === item.masterModelId,
            ).length > 0,
        )

      // get filtered tails
      const filteredAirlines = airlinesData
        .filter(
          item =>
            airlineIds.length === 0 || airlineIds.includes(item.airlineId),
        )
        .filter(item => allowedAirlineIds.includes(item.airlineId))
      const filteredAirlineNames = filteredAirlines.map(item => item.icaoCode)

      options.airlineIcaos = filteredAirlineNames

      if (options.airlineIcaos.length > 0) {
        const response = await services.dplf.getData(
          'faultMsgCountByTail',
          options,
          true,
        )
        const filteredData = response.data.filter(
          item =>
            filteredTails.filter(tail => tail.text === item.tailNumber).length >
            0,
        )

        commit(DC.UPDATE_FAULT_MSG_COUNT_BY_TAIL, filteredData)
      }
    } catch (error) {
      console.log(DC.UPDATE_FAULT_MSG_COUNT_BY_TAIL + ': error ' + error)
    }
  },

  async [DC.GET_FAULT_MSG_COUNT_BY_ATA_CHAPTER]({ commit }, options) {
    try {
      const response = await services.dplf.getData(
        'faultMsgCountByAtaChapter',
        options,
        true,
      )
      commit(DC.UPDATE_FAULT_MSG_COUNT_BY_ATA_CHAPTER, response.data)
    } catch (error) {
      console.log(DC.UPDATE_FAULT_MSG_COUNT_BY_ATA_CHAPTER + ': error ' + error)
    }
  },

  async [DC.GET_FAULT_MSG_COUNT_BY_PARA]({ commit }, options) {
    try {
      const response = await services.dplf.getData(
        'faultMsgCountByPara',
        options,
        true,
      )
      commit(DC.UPDATE_FAULT_MSG_COUNT_BY_PARA, response.data)
    } catch (error) {
      console.log(DC.UPDATE_FAULT_MSG_COUNT_BY_PARA + ': error ' + error)
    }
  },

  async [DC.GET_FAULT_MSG_CODES]({ commit }, options) {
    try {
      if (options.paras.length > 0) {
        const response = await services.dplf.getData(
          'faultMsgCodes',
          options,
          true,
        )

        const mappedData = response.data.map(item => {
          return {
            ...item,
            departureTime: item.departureTime + 'Z',
            arrivalTime: item.arrivalTime + 'Z',
            ataChapter: options.ataChapter,
            tailNumber: options.tailNumber,
          }
        })

        commit(DC.UPDATE_FAULT_MSG_CODES, mappedData)
      }
    } catch (error) {
      console.log(DC.UPDATE_FAULT_MSG_CODES + ': error ' + error)
    }
  },

  async [DC.GET_FAULT_MSG_RAW_REPORT]({ commit }, options) {
    try {
      const response = await services.dplf.getData(
        'faultMsgRawReport',
        options,
        true,
      )
      const data = response.data
      commit(DC.UPDATE_FAULT_MSG_RAW_REPORT, data)
    } catch (error) {
      console.log(`${DC.GET_FAULT_MSG_RAW_REPORT}: error ${error}`)
    }
  },

  async [DC.GET_MESSAGE_CODES]({ commit }, options) {
    try {
      const response = await services.dplf.getData(
        'messageCodesData',
        options,
        true,
      )
      const data = response.data
      commit(DC.UPDATE_MESSAGE_CODES, data)
    } catch (error) {
      console.log(`${DC.GET_MESSAGE_CODES}: error ${error}`)
    }
  },

  async [DC.GET_FAULT_REPORTS_BY_FLIGHT]({ commit, rootGetters }, options) {
    try {
      // get global filter, user settings and lookups data
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const airlinesData = rootGetters['user/mappedUserAirlines']
      const phmAirlinesData =
        rootGetters['user/mappedUserAirlinesWithPhmAgreements']
      // set allowed airlineIds - airlineIds with Phm Agreements
      const allowedAirlineIds = phmAirlinesData.map(item => item.airlineId)

      // get filtered tails
      const filteredAirlines = airlinesData
        .filter(
          item =>
            airlineIds.length === 0 || airlineIds.includes(item.airlineId),
        )
        .filter(item => allowedAirlineIds.includes(item.airlineId))
      const filteredAirlineNames = filteredAirlines.map(item => item.icaoCode)

      options.airlineIcaos = filteredAirlineNames

      if (options.airlineIcaos.length > 0) {
        const response = await services.dplf.getData(
          'faultReportsByFlight',
          options,
          true,
        )
        const data = response.data

        const mapped = data.map(item => {
          return {
            airlineIcao: item.airlineIcao,
            fileLogId: item.fileLogId,
            flightNumber: item.flightNumber,
            timeStamp: item.departureTime + 'Z',
            tailNumber: item.tailNumber,
            departureAirport: item.departureAirportIcao,
            arrivalAirport: item.arrivalAirportIcao,
            faultMsgCount: item.faultMessageCount,
            children: [],
          }
        })

        commit(DC.UPDATE_FAULT_REPORTS_BY_FLIGHT, mapped)
      }
    } catch (error) {
      console.log(`${DC.GET_FAULT_REPORTS_BY_FLIGHT}: error ${error}`)
    }
  },

  async [DC.GET_FAULT_MSG_DETAILS]({ commit }, options) {
    try {
      const response = await services.dplf.getData(
        'faultMsgDetails',
        options,
        true,
      )

      const mappedData = response.data.map(item => {
        return {
          ...item,
          ataChapter: options.ataChapter,
          tailNumber: options.tailNumber,
        }
      })

      commit(DC.UPDATE_FAULT_MSG_DETAILS, mappedData)
    } catch (error) {
      console.log(`${DC.GET_FAULT_MSG_DETAILS}: error ${error}`)
    }
  },

  async [DC.GET_VISUALIZE_DETAILS]({ commit }, options) {
    try {
      const responseTail = await services.dplf.getData(
        'visualizeDetailsForTail',
        options,
        true,
      )
      const responseFleet = await services.dplf.getData(
        'visualizeDetailsForFleet',
        options,
        true,
      )

      const mappedTailData = responseTail.data.messageCounts.map(item => {
        return {
          tailCount:
            item.fdEcount + item.mmOcount + item.cmMcount + item.ncmMcount,
          timeStamp: new Date(item.date + 'Z').getTime(),
        }
      })

      const mappedFleetData = responseFleet.data.map(item => {
        const timeStamp = new Date(item.date + 'Z').getTime()
        const [tail] = mappedTailData.filter(
          item2 => item2.timeStamp === timeStamp,
        )
        return {
          tailNumber: options.tailNumber,
          fleetCount:
            item.fdEcount + item.mmOcount + item.cmMcount + item.ncmMcount,
          tailCount: tail ? tail.tailCount : 0,
          timeStamp,
        }
      })

      const sortedFleetData = _.orderBy(mappedFleetData, ['timeStamp'], ['asc'])

      const visualizeData = {
        tailNumber: options.tailNumber,
        counts: sortedFleetData,
        messageCode: `${options.errorType} ${options.code}`,
        timelineText: options.timelineText,
      }

      commit(DC.UPDATE_VISUALIZE_DETAILS, visualizeData)
    } catch (error) {
      console.log(`${DC.GET_VISUALIZE_DETAILS}: error ${error}`)
    }
  },

  async [DC.GET_ATA_CHAPTER_OPTIONS_DATA]({ commit }) {
    try {
      const response = await services.dplf.getData('ataChapters', null, false)
      const options = response.data
      options.forEach(ataObj => {
        ataObj.text = ataObj.ataChapter + ' - ' + ataObj.description
      })
      commit(DC.UPDATE_ATA_CHAPTER_OPTIONS, options)
    } catch (error) {
      console.log(DC.GET_ATA_CHAPTER_OPTIONS_DATA + ': error ' + error)
    }
  },
  [DC.RESET_CURRENT_DPLF_VALUES]({ commit }) {
    commit(DC.RESET_DPLF_VALUES)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
